import { create } from "zustand";
import {
  BLOCK_DEFINITIONS,
  getBlockDefinition,
  getDefaultBlockState,
  installVersionBlockCatalog,
  isBlockAvailable,
  resetBlockCatalog
} from "../core/blockRegistry.ts";
import { VoxelWorld } from "../core/VoxelWorld.ts";
import {
  mirrorBlockStateAcrossX,
  placeBlockWithRules,
  removeBlockWithRules
} from "../core/blockPlacement.ts";
import {
  createProtocolExample,
  executeBuildProtocol
} from "../core/BuildProtocol.ts";
import { createMinecraftExport } from "../export/minecraftExport.ts";
import { generateMedievalGatehouse } from "../generators/medievalGatehouse.ts";
import { generateVanillaModelShowcase } from "../generators/vanillaModelShowcase.ts";
import { generateSpecialRendererShowcase } from "../generators/specialRendererShowcase.ts";
import type { VoxelRenderer } from "../render/VoxelRenderer.ts";
import {
  MinecraftOfficialAssetLoader,
  fetchMinecraftReleaseVersions
} from "../render/MinecraftOfficialAssetLoader.ts";
import type {
  MinecraftReleaseVersion
} from "../render/MinecraftOfficialAssetLoader.ts";
import {
  cloneBlockEntityData,
  createDefaultBlockEntityData
} from "../core/blockEntityData.ts";
import {
  captureSelection,
  deleteSelection,
  getSelectionBounds,
  mirrorSelectionClipboardX,
  mirrorSelectionClipboardZ,
  pasteSelection,
  pasteSelectionArray,
  rotateSelectionClipboardY
} from "../core/selectionClipboard.ts";
import type {
  SelectionClipboard
} from "../core/selectionClipboard.ts";
import type {
  BlockEntityData
} from "../core/blockEntityData.ts";
import type {
  BlockState,
  CameraView,
  ExportFormat,
  GridPosition,
  MinecraftOrigin,
  TextureStatus,
  ToolId,
  VoxelHit
} from "../types/editor";

export const TOOL_DEFINITIONS: ReadonlyArray<{
  id: ToolId;
  name: string;
  icon: string;
  shortcut: string;
}> = [
  { id: "place", name: "Place", icon: "＋", shortcut: "1" },
  { id: "erase", name: "Erase", icon: "⌫", shortcut: "2" },
  { id: "select", name: "Select", icon: "⌖", shortcut: "3" },
  { id: "sample", name: "Sample", icon: "⌁", shortcut: "4" }
];

export const editorWorld = new VoxelWorld();

type VersionSetupStatus =
  | "idle"
  | "loading-list"
  | "selecting"
  | "loading-project"
  | "ready"
  | "error";

interface EditorStore {
  world: VoxelWorld;
  renderer: VoxelRenderer | null;
  assetLoader: MinecraftOfficialAssetLoader | null;
  worldRevision: number;
  availableVersions: MinecraftReleaseVersion[];
  latestVersionId: string | null;
  selectedVersionId: string;
  projectVersionId: string | null;
  versionStatus: VersionSetupStatus;
  versionError: string;
  blockCatalogRevision: number;
  blockSearch: string;
  blockCategory: string;
  activeTool: ToolId;
  activeBlockId: string;
  activeBlockState: BlockState;
  activeBlockEntityData: BlockEntityData | null;
  selectedPosition: GridPosition | null;
  selectionEnd: GridPosition | null;
  selectionClipboard: SelectionClipboard | null;
  selectionTransformOffset: GridPosition;
  selectionArrayCopies: number;
  brushSize: number;
  mirrorX: boolean;
  visibleMaximumY: number;
  cursorPosition: GridPosition | null;
  textureStatus: TextureStatus;
  protocolInput: string;
  exportFormat: ExportFormat;
  exportOrigin: MinecraftOrigin;
  exportOutput: string;
  exportCommandCount: number;
  toastMessage: string;
  toastNonce: number;

  bindRenderer(renderer: VoxelRenderer | null): void;
  loadVersionOptions(): Promise<void>;
  setSelectedVersion(versionId: string): void;
  startVersionedProject(versionId?: string): Promise<void>;
  resetVersionedProject(): void;
  setBlockSearch(value: string): void;
  setBlockCategory(value: string): void;
  setActiveTool(tool: ToolId): void;
  setActiveBlock(
    blockId: string,
    stateOverride?: BlockState,
    blockEntityOverride?: BlockEntityData | null
  ): void;
  setActiveBlockStateValue(key: string, value: string): void;
  setSelectedPosition(position: GridPosition | null): void;
  setSelectionEnd(position: GridPosition | null): void;
  handleSelectionDrag(
    startHit: VoxelHit | null,
    endHit: VoxelHit | null,
    complete: boolean
  ): void;
  setSelectionTransformOffset(
    axis: keyof GridPosition,
    value: number
  ): void;
  setSelectionArrayCopies(value: number): void;
  moveSelectionByOffset(): void;
  duplicateSelectionByOffset(): void;
  arraySelectionByOffset(): void;
  copySelection(): void;
  cutSelection(): void;
  pasteClipboard(): void;
  rotateClipboard(): void;
  mirrorClipboardX(): void;
  mirrorClipboardZ(): void;
  setSelectedBlockEntityData(
    data: BlockEntityData | null
  ): void;
  initializeSelectedBlockEntityData(): void;
  setBrushSize(size: number): void;
  setMirrorX(enabled: boolean): void;
  setVisibleMaximumY(y: number): void;
  setCursorPosition(position: GridPosition | null): void;
  setTextureStatus(status: TextureStatus): void;
  setProtocolInput(value: string): void;
  setExportFormat(format: ExportFormat): void;
  setExportOrigin(axis: keyof MinecraftOrigin, value: number): void;
  refreshExport(): void;
  notify(message: string): void;
  clearToast(): void;
  handleViewportHit(hit: VoxelHit | null): void;
  deleteSelected(): void;
  generateGatehouse(): void;
  generateModelLab(): void;
  generateSpecialRendererLab(): void;
  clearWorld(): void;
  runProtocol(): number;
  undo(): void;
  redo(): void;
  frameWorld(): void;
  setCameraView(view: CameraView): void;
  saveProjectJson(): void;
}

function getSelectablePosition(
  hit: VoxelHit | null
): GridPosition | null {
  if (!hit) return null;
  const position = hit.isGround ? hit.adjacent : hit.position;
  return {
    x: Math.round(position.x),
    y: Math.round(position.y),
    z: Math.round(position.z)
  };
}

function offsetPosition(
  position: GridPosition,
  offset: GridPosition,
  multiplier = 1
): GridPosition {
  return {
    x: position.x + offset.x * multiplier,
    y: position.y + offset.y * multiplier,
    z: position.z + offset.z * multiplier
  };
}

function isZeroOffset(offset: GridPosition): boolean {
  return offset.x === 0 && offset.y === 0 && offset.z === 0;
}

function forEachBrushVoxel(
  center: GridPosition,
  brushSize: number,
  callback: (position: GridPosition) => void
): void {
  const effectiveRadius = brushSize - 1;
  for (let y = -effectiveRadius; y <= effectiveRadius; y += 1) {
    for (let z = -effectiveRadius; z <= effectiveRadius; z += 1) {
      for (let x = -effectiveRadius; x <= effectiveRadius; x += 1) {
        if (Math.abs(x) + Math.abs(y) + Math.abs(z) > effectiveRadius + 1) {
          continue;
        }
        callback({
          x: center.x + x,
          y: center.y + y,
          z: center.z + z
        });
      }
    }
  }
}

export const useEditorStore = create<EditorStore>((set, get) => ({
  world: editorWorld,
  renderer: null,
  assetLoader: null,
  worldRevision: 0,
  availableVersions: [],
  latestVersionId: null,
  selectedVersionId: "",
  projectVersionId: null,
  versionStatus: "idle",
  versionError: "",
  blockCatalogRevision: 0,
  blockSearch: "",
  blockCategory: "All",
  activeTool: "place",
  activeBlockId: "stone_bricks",
  activeBlockState: getDefaultBlockState("stone_bricks"),
  activeBlockEntityData: null,
  selectedPosition: null,
  selectionEnd: null,
  selectionClipboard: null,
  selectionTransformOffset: { x: 8, y: 0, z: 0 },
  selectionArrayCopies: 3,
  brushSize: 1,
  mirrorX: false,
  visibleMaximumY: 64,
  cursorPosition: null,
  textureStatus: {
    status: "idle",
    detail: "Official Minecraft assets not loaded yet",
    versionId: null
  },
  protocolInput: JSON.stringify(createProtocolExample(), null, 2),
  exportFormat: "mcfunction",
  exportOrigin: { x: 0, y: 64, z: 0 },
  exportOutput: "",
  exportCommandCount: 0,
  toastMessage: "",
  toastNonce: 0,

  bindRenderer(renderer) {
    set({ renderer });
  },

  async loadVersionOptions() {
    const current = get();
    if (
      current.versionStatus === "loading-list" ||
      current.availableVersions.length > 0
    ) {
      return;
    }

    set({
      versionStatus: "loading-list",
      versionError: ""
    });

    try {
      const result = await fetchMinecraftReleaseVersions();
      set({
        availableVersions: result.versions,
        latestVersionId: result.latestRelease,
        selectedVersionId:
          current.selectedVersionId || result.latestRelease,
        versionStatus: "selecting"
      });
    } catch (error) {
      set({
        versionStatus: "error",
        versionError:
          error instanceof Error ? error.message : String(error)
      });
    }
  },

  setSelectedVersion(selectedVersionId) {
    if (get().projectVersionId) return;
    set({ selectedVersionId });
  },

  async startVersionedProject(versionId) {
    const targetVersion =
      versionId || get().selectedVersionId || get().latestVersionId;
    if (!targetVersion) {
      set({
        versionStatus: "error",
        versionError: "Choose a Minecraft Java version first."
      });
      return;
    }

    set({
      versionStatus: "loading-project",
      versionError: "",
      selectedVersionId: targetVersion,
      textureStatus: {
        status: "loading",
        detail: "Loading Minecraft Java " + targetVersion,
        versionId: targetVersion
      }
    });

    try {
      const loader = new MinecraftOfficialAssetLoader(targetVersion);
      loader.setStatusListener((textureStatus) => {
        set({ textureStatus });
      });

      await loader.initializeModelLibrary();
      const catalog = await loader.buildBlockCatalog();
      if (catalog.length === 0) {
        throw new Error(
          "No vanilla blockstates were discovered for Java " +
            targetVersion +
            "."
        );
      }

      installVersionBlockCatalog(catalog);

      if (editorWorld.blocks.size > 0) {
        editorWorld.clear();
      }
      editorWorld.undoStack.length = 0;
      editorWorld.redoStack.length = 0;
      editorWorld.setMinecraftVersion(targetVersion);

      const initialBlockId = isBlockAvailable("stone_bricks")
        ? "stone_bricks"
        : BLOCK_DEFINITIONS[0]?.id;

      if (!initialBlockId) {
        throw new Error("The selected version exposed no buildable blocks.");
      }

      set((state) => ({
        assetLoader: loader,
        projectVersionId: targetVersion,
        versionStatus: "ready",
        blockCatalogRevision: state.blockCatalogRevision + 1,
        activeBlockId: initialBlockId,
        activeBlockState: getDefaultBlockState(initialBlockId),
        activeBlockEntityData: null,
        blockSearch: "",
        blockCategory: "All",
        selectedPosition: null,
        selectionEnd: null,
        selectionClipboard: null,
        selectionTransformOffset: { x: 8, y: 0, z: 0 },
        selectionArrayCopies: 3
      }));

      get().refreshExport();
      get().notify(
        "Java " +
          targetVersion +
          " ready · " +
          catalog.length.toLocaleString() +
          " vanilla blocks discovered"
      );
    } catch (error) {
      resetBlockCatalog();
      editorWorld.setMinecraftVersion(null);
      set((state) => ({
        assetLoader: null,
        projectVersionId: null,
        versionStatus: "error",
        versionError:
          error instanceof Error ? error.message : String(error),
        blockCatalogRevision: state.blockCatalogRevision + 1
      }));
    }
  },

  resetVersionedProject() {
    if (editorWorld.blocks.size > 0) {
      editorWorld.clear();
    }
    editorWorld.undoStack.length = 0;
    editorWorld.redoStack.length = 0;
    editorWorld.setMinecraftVersion(null);
    resetBlockCatalog();

    set((state) => ({
      renderer: null,
      assetLoader: null,
      projectVersionId: null,
      versionStatus:
        state.availableVersions.length > 0 ? "selecting" : "idle",
      versionError: "",
      blockCatalogRevision: state.blockCatalogRevision + 1,
      selectedPosition: null,
      selectionEnd: null,
      selectionClipboard: null,
      selectionTransformOffset: { x: 8, y: 0, z: 0 },
      selectionArrayCopies: 3,
      activeBlockEntityData: null,
      blockSearch: "",
      blockCategory: "All"
    }));
  },

  setBlockSearch(blockSearch) {
    set({ blockSearch });
  },

  setBlockCategory(blockCategory) {
    set({ blockCategory });
  },

  setActiveTool(activeTool) {
    set({ activeTool });
  },

  setActiveBlock(
    blockId,
    stateOverride = {},
    blockEntityOverride = null
  ) {
    set({
      activeBlockId: blockId,
      activeBlockState: {
        ...getDefaultBlockState(blockId),
        ...stateOverride
      },
      activeBlockEntityData:
        cloneBlockEntityData(blockEntityOverride)
    });
  },

  setActiveBlockStateValue(key, value) {
    set((state) => ({
      activeBlockState: {
        ...state.activeBlockState,
        [key]: value
      }
    }));
  },

  setSelectedPosition(selectedPosition) {
    set({
      selectedPosition,
      selectionEnd: null
    });
    get().renderer?.setSelectionRegion(
      selectedPosition,
      selectedPosition
    );
  },

  setSelectionEnd(selectionEnd) {
    const start = get().selectedPosition;
    if (!start) {
      set({ selectionEnd: null });
      get().renderer?.setSelectionRegion(null, null);
      return;
    }
    set({ selectionEnd });
    get().renderer?.setSelectionRegion(
      start,
      selectionEnd || start
    );
  },

  handleSelectionDrag(startHit, endHit, complete) {
    const state = get();
    if (state.activeTool !== "select") return;

    const start = getSelectablePosition(startHit);
    const end = getSelectablePosition(endHit);
    if (!start || !end) return;

    set({
      selectedPosition: start,
      selectionEnd: end
    });
    state.renderer?.setSelectionRegion(start, end);

    if (complete) {
      const bounds = getSelectionBounds(start, end);
      state.notify(
        "Drag selected " +
          bounds.volume.toLocaleString() +
          (bounds.volume === 1 ? " voxel" : " voxels") +
          " · " +
          bounds.size.x +
          "×" +
          bounds.size.y +
          "×" +
          bounds.size.z
      );
    }
  },

  setSelectionTransformOffset(axis, value) {
    const normalized = Number.isFinite(value)
      ? Math.max(-1024, Math.min(1024, Math.round(value)))
      : 0;
    set((state) => ({
      selectionTransformOffset: {
        ...state.selectionTransformOffset,
        [axis]: normalized
      }
    }));
  },

  setSelectionArrayCopies(value) {
    const normalized = Number.isFinite(value)
      ? Math.max(1, Math.min(64, Math.round(value)))
      : 1;
    set({ selectionArrayCopies: normalized });
  },

  moveSelectionByOffset() {
    const state = get();
    const start = state.selectedPosition;
    if (!start) {
      state.notify("Select a block or region first");
      return;
    }
    if (isZeroOffset(state.selectionTransformOffset)) {
      state.notify("Set a non-zero transform offset first");
      return;
    }

    const end = state.selectionEnd || start;
    const bounds = getSelectionBounds(start, end);
    const clipboard = captureSelection(editorWorld, start, end);
    if (clipboard.voxels.length === 0) {
      state.notify("Selection contains no blocks");
      return;
    }

    const targetOrigin = offsetPosition(
      bounds.min,
      state.selectionTransformOffset
    );
    const targetEnd = {
      x: targetOrigin.x + bounds.size.x - 1,
      y: targetOrigin.y + bounds.size.y - 1,
      z: targetOrigin.z + bounds.size.z - 1
    };

    editorWorld.beginTransaction("Move selection");
    deleteSelection(editorWorld, start, end);
    pasteSelection(editorWorld, clipboard, targetOrigin);
    const changed = editorWorld.commitTransaction();

    set({
      selectedPosition: targetOrigin,
      selectionEnd: targetEnd
    });
    state.renderer?.setSelectionRegion(targetOrigin, targetEnd);
    state.refreshExport();
    state.notify(
      "Moved selection · " +
        changed.toLocaleString() +
        " changed voxels"
    );
  },

  duplicateSelectionByOffset() {
    const state = get();
    const start = state.selectedPosition;
    if (!start) {
      state.notify("Select a block or region first");
      return;
    }
    if (isZeroOffset(state.selectionTransformOffset)) {
      state.notify("Set a non-zero transform offset first");
      return;
    }

    const end = state.selectionEnd || start;
    const bounds = getSelectionBounds(start, end);
    const clipboard = captureSelection(editorWorld, start, end);
    if (clipboard.voxels.length === 0) {
      state.notify("Selection contains no blocks");
      return;
    }

    const targetOrigin = offsetPosition(
      bounds.min,
      state.selectionTransformOffset
    );
    const targetEnd = {
      x: targetOrigin.x + bounds.size.x - 1,
      y: targetOrigin.y + bounds.size.y - 1,
      z: targetOrigin.z + bounds.size.z - 1
    };

    editorWorld.beginTransaction("Duplicate selection");
    pasteSelection(editorWorld, clipboard, targetOrigin);
    const changed = editorWorld.commitTransaction();

    set({
      selectedPosition: targetOrigin,
      selectionEnd: targetEnd
    });
    state.renderer?.setSelectionRegion(targetOrigin, targetEnd);
    state.refreshExport();
    state.notify(
      "Duplicated selection · " +
        changed.toLocaleString() +
        " changed voxels"
    );
  },

  arraySelectionByOffset() {
    const state = get();
    const start = state.selectedPosition;
    if (!start) {
      state.notify("Select a block or region first");
      return;
    }
    if (isZeroOffset(state.selectionTransformOffset)) {
      state.notify("Set a non-zero array offset first");
      return;
    }

    const end = state.selectionEnd || start;
    const bounds = getSelectionBounds(start, end);
    const clipboard = captureSelection(editorWorld, start, end);
    if (clipboard.voxels.length === 0) {
      state.notify("Selection contains no blocks");
      return;
    }

    editorWorld.beginTransaction("Linear array selection");
    pasteSelectionArray(
      editorWorld,
      clipboard,
      bounds.min,
      state.selectionTransformOffset,
      state.selectionArrayCopies
    );
    const changed = editorWorld.commitTransaction();

    const lastOrigin = offsetPosition(
      bounds.min,
      state.selectionTransformOffset,
      state.selectionArrayCopies
    );
    const lastEnd = {
      x: lastOrigin.x + bounds.size.x - 1,
      y: lastOrigin.y + bounds.size.y - 1,
      z: lastOrigin.z + bounds.size.z - 1
    };
    const arrayMin = {
      x: Math.min(bounds.min.x, lastOrigin.x),
      y: Math.min(bounds.min.y, lastOrigin.y),
      z: Math.min(bounds.min.z, lastOrigin.z)
    };
    const arrayMax = {
      x: Math.max(bounds.max.x, lastEnd.x),
      y: Math.max(bounds.max.y, lastEnd.y),
      z: Math.max(bounds.max.z, lastEnd.z)
    };

    set({
      selectedPosition: arrayMin,
      selectionEnd: arrayMax
    });
    state.renderer?.setSelectionRegion(arrayMin, arrayMax);
    state.refreshExport();
    state.notify(
      "Created linear array · " +
        state.selectionArrayCopies.toLocaleString() +
        " copies · " +
        changed.toLocaleString() +
        " changed voxels"
    );
  },

  copySelection() {
    const state = get();
    const start = state.selectedPosition;
    if (!start) {
      state.notify("Select a block or region first");
      return;
    }
    const end = state.selectionEnd || start;
    const clipboard = captureSelection(editorWorld, start, end);
    if (clipboard.voxels.length === 0) {
      state.notify("Selection contains no blocks");
      return;
    }
    set({ selectionClipboard: clipboard });
    state.notify(
      "Copied " +
        clipboard.voxels.length.toLocaleString() +
        (clipboard.voxels.length === 1 ? " block" : " blocks")
    );
  },

  cutSelection() {
    const state = get();
    const start = state.selectedPosition;
    if (!start) {
      state.notify("Select a block or region first");
      return;
    }
    const end = state.selectionEnd || start;
    const clipboard = captureSelection(editorWorld, start, end);
    if (clipboard.voxels.length === 0) {
      state.notify("Selection contains no blocks");
      return;
    }

    editorWorld.beginTransaction("Cut selection");
    const removed = deleteSelection(editorWorld, start, end);
    editorWorld.commitTransaction();

    set({ selectionClipboard: clipboard });
    state.setSelectedPosition(null);
    state.refreshExport();
    state.notify(
      "Cut " +
        removed.toLocaleString() +
        (removed === 1 ? " block" : " blocks")
    );
  },

  pasteClipboard() {
    const state = get();
    const clipboard = state.selectionClipboard;
    if (!clipboard || clipboard.voxels.length === 0) {
      state.notify("Internal clipboard is empty");
      return;
    }

    if (
      clipboard.sourceVersion &&
      editorWorld.minecraftVersion &&
      clipboard.sourceVersion !== editorWorld.minecraftVersion
    ) {
      state.notify(
        "Clipboard belongs to Java " +
          clipboard.sourceVersion +
          "; copy again for this project version"
      );
      return;
    }

    const origin =
      state.cursorPosition || state.selectedPosition;
    if (!origin) {
      state.notify("Move the cursor to a paste origin first");
      return;
    }

    editorWorld.beginTransaction("Paste selection");
    const changed = pasteSelection(
      editorWorld,
      clipboard,
      origin
    );
    editorWorld.commitTransaction();

    const end = {
      x: origin.x + clipboard.size.x - 1,
      y: origin.y + clipboard.size.y - 1,
      z: origin.z + clipboard.size.z - 1
    };
    set({
      selectedPosition: { ...origin },
      selectionEnd: end
    });
    state.renderer?.setSelectionRegion(origin, end);
    state.refreshExport();
    state.notify(
      "Pasted " +
        changed.toLocaleString() +
        (changed === 1 ? " changed block" : " changed blocks")
    );
  },

  rotateClipboard() {
    const clipboard = get().selectionClipboard;
    if (!clipboard) {
      get().notify("Internal clipboard is empty");
      return;
    }
    const rotated = rotateSelectionClipboardY(
      clipboard,
      90
    );
    set({ selectionClipboard: rotated });
    get().notify(
      "Clipboard rotated 90° · " +
        rotated.size.x +
        "×" +
        rotated.size.y +
        "×" +
        rotated.size.z
    );
  },

  mirrorClipboardX() {
    const clipboard = get().selectionClipboard;
    if (!clipboard) {
      get().notify("Internal clipboard is empty");
      return;
    }
    set({
      selectionClipboard:
        mirrorSelectionClipboardX(clipboard)
    });
    get().notify("Clipboard mirrored across X");
  },

  mirrorClipboardZ() {
    const clipboard = get().selectionClipboard;
    if (!clipboard) {
      get().notify("Internal clipboard is empty");
      return;
    }
    set({
      selectionClipboard:
        mirrorSelectionClipboardZ(clipboard)
    });
    get().notify("Clipboard mirrored across Z");
  },

  setSelectedBlockEntityData(data) {
    const position = get().selectedPosition;
    if (!position || !editorWorld.hasBlock(
      position.x,
      position.y,
      position.z
    )) {
      return;
    }

    editorWorld.beginTransaction("Edit Block Entity");
    editorWorld.setBlockEntityData(
      position.x,
      position.y,
      position.z,
      data
    );
    const changed = editorWorld.commitTransaction();

    if (changed > 0) {
      get().refreshExport();
    }
  },

  initializeSelectedBlockEntityData() {
    const position = get().selectedPosition;
    if (!position) return;

    const blockId = editorWorld.getBlock(
      position.x,
      position.y,
      position.z
    );
    if (!blockId) return;

    const existing = editorWorld.getBlockEntityData(
      position.x,
      position.y,
      position.z
    );
    if (existing) return;

    const initial = createDefaultBlockEntityData(blockId);
    if (initial) {
      get().setSelectedBlockEntityData(initial);
    }
  },

  setBrushSize(brushSize) {
    set({ brushSize: Math.max(1, Math.min(4, Math.round(brushSize))) });
  },

  setMirrorX(mirrorX) {
    set({ mirrorX });
  },

  setVisibleMaximumY(visibleMaximumY) {
    set({ visibleMaximumY });
    get().renderer?.setVisibleMaximumY(visibleMaximumY);
  },

  setCursorPosition(cursorPosition) {
    set({ cursorPosition });
  },

  setTextureStatus(textureStatus) {
    set({ textureStatus });
  },

  setProtocolInput(protocolInput) {
    set({ protocolInput });
  },

  setExportFormat(exportFormat) {
    set({ exportFormat });
    queueMicrotask(() => get().refreshExport());
  },

  setExportOrigin(axis, value) {
    set((state) => ({
      exportOrigin: {
        ...state.exportOrigin,
        [axis]: Number.isFinite(value) ? value : 0
      }
    }));
    queueMicrotask(() => get().refreshExport());
  },

  refreshExport() {
    const state = get();
    const result = createMinecraftExport(
      editorWorld,
      state.exportFormat,
      state.exportOrigin
    );
    set({
      exportOutput: result.text,
      exportCommandCount: result.commandCount
    });
  },

  notify(toastMessage) {
    set((state) => ({
      toastMessage,
      toastNonce: state.toastNonce + 1
    }));
  },

  clearToast() {
    set({ toastMessage: "" });
  },

  handleViewportHit(hit) {
    const state = get();

    if (!hit) {
      if (state.activeTool === "select") state.setSelectedPosition(null);
      return;
    }

    if (state.activeTool === "select") {
      const selectablePosition = getSelectablePosition(hit);
      if (!selectablePosition) return;

      if (!state.selectedPosition || state.selectionEnd) {
        state.setSelectedPosition(selectablePosition);
        state.notify("Selection start set · choose the opposite corner");
      } else {
        state.setSelectionEnd(selectablePosition);
        const bounds = getSelectionBounds(
          state.selectedPosition,
          selectablePosition
        );
        state.notify(
          "Selected " +
            bounds.volume.toLocaleString() +
            (bounds.volume === 1 ? " voxel" : " voxels") +
            " · " +
            bounds.size.x +
            "×" +
            bounds.size.y +
            "×" +
            bounds.size.z
        );
      }
      return;
    }

    if (hit.isGround && state.activeTool !== "place") return;

    if (state.activeTool === "sample") {
      if (!hit.blockId) return;
      state.setActiveBlock(
        hit.blockId,
        hit.state ?? {},
        editorWorld.getBlockEntityData(
          hit.position.x,
          hit.position.y,
          hit.position.z
        )
      );
      state.setActiveTool("place");
      state.notify("Sampled " + (getBlockDefinition(hit.blockId)?.name ?? hit.blockId));
      return;
    }

    const center =
      state.activeTool === "erase" ? hit.position : hit.adjacent;

    editorWorld.beginTransaction(
      state.activeTool === "erase" ? "Erase blocks" : "Place blocks"
    );

    editorWorld.runBatch(() => {
      forEachBrushVoxel(center, state.brushSize, (position) => {
        const edit = (
          editablePosition: GridPosition,
          mirrored: boolean
        ) => {
          if (state.activeTool === "erase") {
            removeBlockWithRules(editorWorld, editablePosition);
            return;
          }

          const placementState = mirrored
            ? mirrorBlockStateAcrossX(
                state.activeBlockId,
                state.activeBlockState
              )
            : state.activeBlockState;

          placeBlockWithRules(
            editorWorld,
            editablePosition,
            state.activeBlockId,
            placementState
          );

          if (
            state.activeBlockEntityData &&
            editorWorld.hasBlock(
              editablePosition.x,
              editablePosition.y,
              editablePosition.z
            )
          ) {
            editorWorld.setBlockEntityData(
              editablePosition.x,
              editablePosition.y,
              editablePosition.z,
              state.activeBlockEntityData
            );
          }
        };

        edit(position, false);
        if (state.mirrorX && position.x !== 0) {
          edit(
            {
              x: -position.x,
              y: position.y,
              z: position.z
            },
            true
          );
        }
      });
    });

    const changed = editorWorld.commitTransaction();
    if (changed > 0) {
      state.notify(
        (state.activeTool === "erase" ? "Removed " : "Placed ") +
          changed.toLocaleString() +
          (changed === 1 ? " block" : " blocks")
      );
      state.refreshExport();
    }
  },

  deleteSelected() {
    const state = get();
    const position = state.selectedPosition;
    if (!position) return;
    const end = state.selectionEnd || position;

    editorWorld.beginTransaction("Delete selection");
    const changed = deleteSelection(
      editorWorld,
      position,
      end
    );
    editorWorld.commitTransaction();
    state.setSelectedPosition(null);
    state.refreshExport();
    state.notify(
      "Deleted " +
        changed.toLocaleString() +
        (changed === 1 ? " block" : " blocks")
    );
  },

  generateGatehouse() {
    const requiredBlocks = [
      "stone_bricks",
      "polished_andesite",
      "deepslate_tiles",
      "deepslate_tile_stairs",
      "deepslate_tile_slab",
      "spruce_planks",
      "spruce_door",
      "spruce_trapdoor",
      "spruce_fence",
      "glass_pane",
      "lantern"
    ];
    const missing = requiredBlocks.filter(
      (blockId) => !isBlockAvailable(blockId)
    );
    if (missing.length > 0) {
      get().notify(
        "Gatehouse sample needs newer blocks unavailable in Java " +
          get().projectVersionId +
          ": " +
          missing.slice(0, 4).join(", ")
      );
      return;
    }

    if (editorWorld.blocks.size > 0) editorWorld.clear();
    const changed = generateMedievalGatehouse(editorWorld);
    get().renderer?.frameWorld();
    get().refreshExport();
    get().notify(
      "Generated " + changed.toLocaleString() + " architectural edits"
    );
  },

  generateModelLab() {
    const requiredBlocks = [
      "stone_brick_stairs",
      "stone_brick_slab",
      "stone_brick_wall",
      "spruce_fence",
      "glass_pane",
      "spruce_door",
      "spruce_trapdoor",
      "deepslate_tile_stairs",
      "deepslate_tile_slab"
    ];
    const missing = requiredBlocks.filter(
      (blockId) => !isBlockAvailable(blockId)
    );
    if (missing.length > 0) {
      get().notify(
        "Vanilla model lab is unavailable for Java " +
          get().projectVersionId +
          " because " +
          missing.slice(0, 4).join(", ") +
          " are missing."
      );
      return;
    }

    if (editorWorld.blocks.size > 0) editorWorld.clear();
    const changed = generateVanillaModelShowcase(editorWorld);
    get().renderer?.frameWorld();
    get().refreshExport();
    get().notify(
      "Generated vanilla model lab · " +
        changed.toLocaleString() +
        " edits"
    );
  },

  generateSpecialRendererLab() {
    if (editorWorld.blocks.size > 0) editorWorld.clear();

    const changed = generateSpecialRendererShowcase(editorWorld);
    if (changed <= 0) {
      get().notify(
        "No compatible special-renderer blocks were found for Java " +
          get().projectVersionId
      );
      return;
    }

    get().renderer?.frameWorld();
    get().refreshExport();
    get().notify(
      "Generated special renderer lab · " +
        changed.toLocaleString() +
        " edits"
    );
  },

  clearWorld() {
    editorWorld.clear();
    get().setSelectedPosition(null);
    get().refreshExport();
    get().notify("World cleared");
  },

  runProtocol() {
    try {
      const changed = executeBuildProtocol(
        editorWorld,
        get().protocolInput
      );
      get().renderer?.frameWorld();
      get().refreshExport();
      get().notify(
        "MDP applied · " + changed.toLocaleString() + " edits"
      );
      return changed;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error);
      get().notify("Protocol error: " + message);
      return 0;
    }
  },

  undo() {
    if (editorWorld.undo()) {
      get().refreshExport();
      get().notify("Undo");
    }
  },

  redo() {
    if (editorWorld.redo()) {
      get().refreshExport();
      get().notify("Redo");
    }
  },

  frameWorld() {
    get().renderer?.frameWorld();
    get().notify("Camera framed to world");
  },

  setCameraView(view) {
    get().renderer?.setCameraView(view);
  },

  saveProjectJson() {
    const projectText = JSON.stringify(editorWorld.toJSON(), null, 2);
    const blob = new Blob([projectText], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "voxel-studio-project.json";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    get().notify("Project JSON downloaded");
  }
}));

editorWorld.subscribe(() => {
  useEditorStore.setState((state) => ({
    worldRevision: state.worldRevision + 1
  }));
});

useEditorStore.getState().refreshExport();

export { BLOCK_DEFINITIONS };
